import { formatDate } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { zip } from 'rxjs';
import { Entity } from 'src/app/classes/entity.class';
import { Project } from 'src/app/classes/project.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import { ApiResponse } from 'src/app/services/api/api.service';
import { RealisationService } from 'src/app/services/realisation.service';
import { Utils } from 'src/app/utils.class';

@Component({
    selector: 'app-copy-realisation-from-other-day',
    templateUrl: './copy-realisation-from-other-day.component.html',
    styleUrls: ['./copy-realisation-from-other-day.component.scss'],
    standalone: false,
})
export class CopyRealisationFromOtherDayComponent extends CodaltComponent implements OnInit {
    fcEntities = new FormControl();

    realisationList: PreviousUsedEntity[];

    constructor(
        public dialogRef: MatDialogRef<CopyRealisationFromOtherDayComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            project: Project;
            date: Date;
            realisations: Realisation[];
        },
        private realisationService: RealisationService
    ) {
        super();
    }

    ngOnInit(): void {
        const realisationCalls$ = [];
        for (let i = 1; i <= 3; i++) {
            const date = new Date(this.data.date);
            date.setDate(date.getDate() - i);
            realisationCalls$.push(this.realisationService.getDaySchedule(date));
        }
        const currentEntityIds = this.data.realisations
            .filter(r => !r.planning_has_entity_id && !!r.entity_id && !r.hiring_name)
            .map(r => r.entity_id);
        this.subscriptions.add(
            zip(...realisationCalls$).subscribe(zipped => {
                const realisationList = [];
                zipped.forEach((realisations: ApiResponse<Realisation[]>) => {
                    realisationList.push(
                        ...realisations.data.filter(
                            r =>
                                !currentEntityIds.includes(r.entity_id) &&
                                !r.planning_has_entity_id &&
                                !!r.entity_id &&
                                r.project?.afas_project_id === this.data.project.afas_project_id
                        )
                    );
                });

                const entities = [] as PreviousUsedEntity[];
                realisationList.forEach(realisation => {
                    const entity = entities.find(
                        e =>
                            e.entity.id === realisation.entity_id &&
                            (!realisation.hiring_name || realisation.hiring_name === e.realisations[0].hiring_name)
                    );
                    if (entity) {
                        entity.realisations.push(realisation);
                    } else {
                        const all = realisationList.filter(
                            r => r.entity_id === realisation.entity.id && r.hiring_name === realisation.hiring_name
                        );
                        const currentCount = this.data.realisations.filter(
                            r => r.entity_id === realisation.entity.id && r.hiring_name === realisation.hiring_name
                        )?.length;
                        const counts = new Map<string, number>();
                        all.forEach(r => {
                            counts.set(
                                formatDate(r.bookdate, 'yyyy-MM-dd', 'nl'),
                                (counts.get(formatDate(r.bookdate, 'yyyy-MM-dd', 'nl')) ?? 0) + 1
                            );
                        });
                        const countAvailable = Math.max(...Array.from(counts.values())) - currentCount;
                        if (countAvailable > 0) {
                            entities.push({
                                name: realisation.hiring_name ?? realisation.entity.name,
                                count: countAvailable,
                                entity: realisation.entity,
                                realisations: [realisation],
                            });
                        }
                    }
                });
                this.realisationList = entities;
            })
        );
    }

    take() {
        const saveCalls$ = [];
        if (this.fcEntities.value?.length) {
            this.fcEntities.value.forEach((pEntity: PreviousUsedEntity) => {
                const realisation = new Realisation();
                realisation.hiring_name = pEntity.realisations[0].hiring_name;
                realisation.entity_id = pEntity.entity.id;
                realisation.afas_project_id = this.data.project.afas_project_id;
                realisation.hourtype = RealisationHourtype.worktime;
                realisation.bookdate = this.data.date;
                realisation.begindate = Utils.setTimeNewDate(this.data.date, 7, 0);
                realisation.enddate = Utils.addMinutes(realisation.begindate, 60 * 8);
                saveCalls$.push(this.realisationService.saveRealisation(realisation));
            });
            this.subscriptions.add(
                zip(...saveCalls$).subscribe(
                    (
                        realisations: ApiResponse<{
                            realisation: Realisation;
                        }>[]
                    ) => {
                        this.dialogRef.close(realisations[0].data.realisation);
                    }
                )
            );
        } else {
            this.dialogRef.close();
        }
    }
}

export class PreviousUsedEntity {
    name: string;
    count: number;
    realisations: Realisation[];
    entity: Entity;
}
